import { fmtCompactNumber, isAbbreviated } from './formatCompactNumber';

/**
 * Formata um peso em kg com truncamento (nunca arredonda para cima).
 * Retorna { value, unit } com unit sempre "kg".
 *
 * Regras (GR-548):
 *  - Valores >= 1 kg  → inteiro truncado, sem decimais
 *  - Valores < 1 kg   → até 3 casas decimais, truncado
 *  - Nunca toneladas
 */
export function fmtWeight(kg: number): { value: string; unit: string } {
  if (!kg || isNaN(kg) || kg < 0) return { value: '0', unit: 'kg' };
  if (kg < 1) {
    const truncated = Math.trunc(kg * 1000) / 1000;
    return {
      value: truncated.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 }),
      unit: 'kg',
    };
  }
  return {
    value: Math.trunc(kg).toLocaleString('pt-BR'),
    unit: 'kg',
  };
}

/**
 * Peso para os KPIs: abrevia a partir de 1 milhão de kg ("11,4 mi"), abaixo
 * disso segue a regra de `fmtWeight`. Unidade continua "kg" — GR-548 proíbe
 * toneladas, então a abreviação é do número, não da unidade.
 */
export function fmtWeightCompact(kg: number): { value: string; unit: string } {
  if (isAbbreviated(kg)) return { value: fmtCompactNumber(kg), unit: 'kg' };
  return fmtWeight(kg);
}

/** Retorna a string completa "X kg" — útil em tooltips e texto inline. */
export function fmtWeightStr(kg: number): string {
  const { value, unit } = fmtWeight(kg);
  return `${value} ${unit}`;
}

/**
 * Versão compacta para eixos de gráficos (sem unidade explícita).
 * 1500 → "1,5k" | 1000000 → "1M" | 500 → "500"
 */
export function fmtWeightAxis(kg: number): string {
  if (!kg || kg <= 0) return '0';
  if (kg >= 1_000_000) {
    const v = Math.trunc(kg / 100_000) / 10;
    return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  }
  if (kg >= 1_000) {
    const v = Math.trunc(kg / 100) / 10;
    return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  }
  return `${Math.trunc(kg).toLocaleString('pt-BR')}`;
}
